import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  History,
  Play,
  CheckSquare,
  Square,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Folder,
  FolderOpen,
  Search,
  Filter as FilterIcon,
  List,
  Grid,
  Sparkles,
  UserPlus,
  ListFilterIcon,
  X,
} from "lucide-react";
import { testcaseService } from "../../services/testcaseService";
import { logger } from "../../services/logService";
import TestCasesEmptyState from "./TestCasesEmptyState";
import "./SectionBasedTestCaseView.css";
import { Switch } from "../ui/switch";
import SkeletonLoader from "../ui/SkeletonLoader";
import Pagination from "../ui/Pagination";
import { ReactComponent as LogsIcon } from "../../assets/icons/logs.svg";
import TestCaseHistoryPage from "../../pages/TestCaseHistoryPage";
import { useNavigate } from "react-router-dom";
import TableSkeleton from "../skeletons/TableSkeleton";

/**
 * Priority badge component
 */
const PriorityBadge = ({ priority }) => {
  const config = {
    critical: {
      bg: "bg-red-100",
      text: "text-red-700",
      label: "Critical",
    },
    high: { bg: "bg-orange-100", text: "text-orange-700", label: "High" },
    medium: {
      bg: "bg-yellow-100",
      text: "text-yellow-700",
      label: "Medium",
    },
    low: { bg: "bg-green-100", text: "text-green-700", label: "Low" },
  };

  const style = config[priority?.toLowerCase()] || config.medium;

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}
    >
      {style.label}
    </span>
  );
};

/**
 * Automation status badge component
 */
const AutomationBadge = ({ status }) => {
  const config = {
    automated: {
      bg: "bg-green-100",
      text: "text-green-700",
      label: "Automated",
      icon: Play,
    },
    partial: {
      bg: "bg-yellow-100",
      text: "text-yellow-700",
      label: "Partial",
      icon: Play,
    },
    not_automated: {
      bg: "bg-gray-100",
      text: "text-gray-700",
      label: "Manual",
      icon: null,
    },
  };

  const style = config[status] || config.not_automated;
  const Icon = style.icon;

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}
    >
      {Icon && <Icon className="w-3 h-3 mr-1" />}
      {style.label}
    </span>
  );
};

/**
 * Label badge component
 */
const LabelBadge = ({ label }) => (
  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700 mr-1">
    {label}
  </span>
);

/**
 * Hierarchical section row with expand/collapse and inline test cases
 */
const HierarchicalSectionRow = ({
  testCasesData = {},
  onOpenHistory,
  navigate,
  projectId,
  section,
  level = 0,
  filters,
  branchId,
  // testCases=[],
  searchQuery = "",
  setSearchQuery,
  selectedSection,
  setShowInactive,
  showInactive,
  onOpenFilter,
  setViewMode,
  viewMode,
  onOpenAI,
  selectedTestCases,
  expandedSections,
  onToggleSection,
  onSelectTestCase,
  onSelectAll,
  onViewTestCase,
  onEditTestCase,
  onDeleteTestCase,
  onAssignTestCase,
  onEditSection,
  onAddCase,
  onAddSubsection,
  onDeleteSection,
  canEdit,
  canDelete,
  canAssign,
  canAdd,
  selectedSectionId,
  activeTestCaseId, // currently open test case id (compact mode)
  showSelectionCheckboxes = false,
}) => {
  const [testCases, setTestCases] = useState([])
  const [testCaseLoading, setTestCaseLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [hoveredTestCaseId, setHoveredTestCaseId] = useState(null)
  const [totalTestCases, setTotalTestCases] = useState(0)
  const [hasBeenVisible, setHasBeenVisible] = useState(false) // true once row enters viewport
  const [fetchedKey, setFetchedKey] = useState(null) // tracks last fetched deps key
  const rowRef = useRef(null) // ref on the section header for IntersectionObserver

  // Build a stable cache key from deps — if it changes, re-fetch
  const depsKey = useMemo(
    () => JSON.stringify({ projectId, sectionId: section.section_id, selectedSectionId, filters, showInactive, branchId, currentPage }),
    [projectId, section.section_id, selectedSectionId, filters, showInactive, branchId, currentPage]
  )

  // Observe visibility — set hasBeenVisible once the row scrolls into view
  useEffect(() => {
    if (hasBeenVisible) return // already visible, no need to re-observe
    const el = rowRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setHasBeenVisible(true) },
      { threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasBeenVisible])

  // Fetch only when visible AND deps changed since last fetch
  useEffect(() => {
    if (!hasBeenVisible) return
    if (fetchedKey === depsKey) return // already fetched for these deps

    const fetchTestCases = async () => {
      if (!projectId || !selectedSectionId) {
        setTestCases([]);
        return;
      }
      setTestCaseLoading(true);
      try {
        const response = await testcaseService.getTestCases(projectId, {
          section_id: section.section_id,
          limit: 10,
          page: currentPage,
          branch_id: branchId || filters?.branch_id || undefined,
          is_active: showInactive ? undefined : true,
          ...filters,
        });
        let testCasesArray = [];
        if (response?.data?.test_cases && Array.isArray(response.data.test_cases)) {
          testCasesArray = response.data.test_cases;
        } else if (response?.data && Array.isArray(response.data)) {
          testCasesArray = response.data;
        } else if (Array.isArray(response)) {
          testCasesArray = response;
        }
        setTotalTestCases(response?.data?.pagination?.total ?? 0)
        setTestCases(testCasesArray);
        setFetchedKey(depsKey) // mark as fetched for these deps
      } catch (error) {
        logger.error("[SectionBasedTestCaseView] Error fetching test cases:", error);
        setTestCases([]);
      } finally {
        setTestCaseLoading(false);
      }
    };

    fetchTestCases();
  }, [hasBeenVisible, depsKey, fetchedKey])
  const hasChildren = section.children && section.children.length > 0;
  // Always expand if this is the selected section (root level) or if it's in expandedSections
  const isExpanded =
    expandedSections.has(section.section_id) ||
    (level === 0 && section.section_id === selectedSectionId);

  // Filter test cases by search query (ID or Title)
  const filteredTestCases = useMemo(() => {
    if (!searchQuery.trim()) return testCases;

    const query = searchQuery.toLowerCase();
    return testCases.filter((tc) => {
      const idMatch = tc.test_case_id?.toLowerCase().includes(query);
      const titleMatch = tc.title?.toLowerCase().includes(query);
      return idMatch || titleMatch;
    });
  }, [testCases, searchQuery]);

  const testCaseCount = filteredTestCases.length;
  const paddingLeft = level * 32; // 32px per level for clear hierarchy

  // Check if all test cases in this section are selected
  const allSelected =
    filteredTestCases.length > 0 &&
    filteredTestCases.every((tc) =>
      selectedTestCases.includes(tc.test_case_id),
    );
  const someSelected = filteredTestCases.some((tc) =>
    selectedTestCases.includes(tc.test_case_id),
  );

  const isVirtualRoot = section.section_id === "__root__";

  if (isVirtualRoot) {
    return (
      <div className="w-full">
        {!activeTestCaseId && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: "row",
            }}
          >
            {/* Left: Search */}
            <div className="w-full sm:w-auto sm:min-w-0 sm:flex-shrink">
              <div className="flex flex-row items-center w-full sm:w-auto">
                <input
                  type="text"
                  placeholder="Search by ID or Title..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-9 w-full sm:min-w-[160px] sm:max-w-[220px] pl-3 pr-3 py-2 text-sm border border-gray-300 rounded-md rounded-r-none focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                />
                <Search className="w-9 h-9 flex-shrink-0 px-2.5 py-2 bg-black text-gray-400 rounded-md rounded-l-none" />
              </div>
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap overflow-x-auto sm:overflow-visible">
              {/* Test Cases Button */}
              <button
                onClick={() => onAddCase(selectedSection)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-full button-brown transition-colors whitespace-nowrap flex-shrink-0"
              >
                <Plus className="w-4 h-4 flex-shrink-0" />
                <span>Test Cases</span>
              </button>

              {/* Test Case AI Button - Only show if onOpenAI is provided (enabled in project settings) */}
              {onOpenAI && (
                <button
                  onClick={() => {
                    if (onOpenAI) {
                      onOpenAI();
                    } else {
                      console.warn("Test Case AI handler not provided");
                    }
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-full button-red transition-colors whitespace-nowrap flex-shrink-0"
                  title="Generate test cases using AI"
                >
                  <Sparkles className="w-4 h-4 flex-shrink-0" />
                  <span>Test Case AI</span>
                </button>
              )}

              {/* Display Inactive Toggle */}
              <button
                onClick={() => setShowInactive(!showInactive)}
                className="flex items-center gap-2 px-2.5 py-2 text-sm font-medium text-gray-700 transition-colors whitespace-nowrap flex-shrink-0"
                title="Display Inactive Test Cases"
              >
                <Switch
                  checked={showInactive}
                  onCheckedChange={() => setShowInactive(!showInactive)}
                />
                <span>Display Inactive</span>
              </button>

              {/* Filter Button */}
              <button
                onClick={() => {
                  if (onOpenFilter) {
                    onOpenFilter();
                  } else {
                    console.warn("Filter handler not provided");
                  }
                }}
                className="flex items-center gap-2 px-2.5 py-2 text-sm font-medium text-gray-700 transition-colors whitespace-nowrap flex-shrink-0"
                title="Filter test cases"
              >
                <ListFilterIcon className="w-4 h-4 flex-shrink-0" />
                <span>Filters</span>
              </button>

              {/* View Toggle */}
              <div className="flex rounded-md overflow-hidden flex-shrink-0">
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 ${viewMode === "list" ? "bg-transparent text-black" : "bg-white text-gray-700 hover:bg-gray-50"} transition-colors`}
                  title="List view"
                >
                  <LogsIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
        {section.children &&
          section.children.map((childSection) => {
            // const childTestCases = testCasesData[childSection.section_id] || [];
            return (
              <HierarchicalSectionRow
                filters={filters}
                branchId={branchId}
                navigate={navigate}
                projectId={projectId}
                key={childSection.section_id}
                setSearchQuery={setSearchQuery}
                setShowInactive={setShowInactive}
                selectedSection={selectedSection}
                showInactive={showInactive}
                onOpenFilter={onOpenFilter}
                setViewMode={setViewMode}
                viewMode={viewMode}
                onOpenAI={onOpenAI}
                onOpenHistory={onOpenHistory}
                testCasesData={testCasesData}
                section={childSection}
                level={0}
                // testCases={childTestCases}
                searchQuery={searchQuery}
                selectedTestCases={selectedTestCases}
                expandedSections={expandedSections}
                onToggleSection={onToggleSection}
                onSelectTestCase={onSelectTestCase}
                onSelectAll={onSelectAll}
                onViewTestCase={onViewTestCase}
                onEditTestCase={onEditTestCase}
                onDeleteTestCase={onDeleteTestCase}
                onAssignTestCase={onAssignTestCase}
                onEditSection={onEditSection}
                onAddCase={onAddCase}
                onAddSubsection={onAddSubsection}
                onDeleteSection={onDeleteSection}
                canEdit={canEdit}
                canDelete={canDelete}
                canAssign={canAssign}
                canAdd={canAdd}
                selectedSectionId={selectedSectionId}
                activeTestCaseId={activeTestCaseId}
                showSelectionCheckboxes={showSelectionCheckboxes}
              />
            );
          })}
      </div>
    );
  }

  return (
    <div className="section-group-fade-in mb-4 w-full">
      {/* Section Header Row */}
      <div
        ref={rowRef}
        data-section-id={section.section_id}
        className="flex items-center justify-between py-3 px-4 bg-[#E3D7D1FF] border rounded-md rounded-br-none rounded-bl-none transition-colors group w-full"
        style={{ paddingLeft: `${16 + paddingLeft}px` }}
      >
        {/* Left: Expand/Collapse + Folder Icon + Section Name */}
        <div className="flex items-center space-x-3 flex-1">
          {/* Expand/Collapse Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleSection(section.section_id);
            }}
            className="p-1 hover:bg-white rounded flex-shrink-0 transition-colors"
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-600" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-600" />
            )}
          </button>

          {/* Section Name and Count */}
          <div className="flex items-center space-x-3">
            <h3 className="text-base font-semibold text-gray-900">
              {section.name}
            </h3>
            <span className="inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-medium bg-[#6B7280FF] text-white">
              {totalTestCases}
            </span>
          </div>
        </div>

        {/* Right: Action Buttons */}
        <div className="flex items-center space-x-2">
          {canEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEditSection(section);
              }}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center space-x-1 opacity-0 group-hover:opacity-100"
              title="Edit section"
            >
              <Edit2 className="w-3 h-3" />
              <span>Edit</span>
            </button>
          )}
          {canAdd && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddCase(section);
                }}
                className="px-3 py-1.5 text-xs font-medium text-indigo-700 bg-white border border-indigo-300 rounded hover:bg-indigo-50 transition-colors flex items-center space-x-1 opacity-0 group-hover:opacity-100"
                title="Add test case"
              >
                <Plus className="w-3 h-3" />
                <span>Add Case</span>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddSubsection(section);
                }}
                className="px-3 py-1.5 text-xs font-medium text-indigo-700 bg-white border border-indigo-300 rounded hover:bg-indigo-50 transition-colors flex items-center space-x-1 opacity-0 group-hover:opacity-100"
                title="Add subsection"
              >
                <Plus className="w-3 h-3" />
                <span>Add Subsection</span>
              </button>
            </>
          )}
        </div>
      </div>

      {
        testCaseLoading && !isVirtualRoot ?
          <SkeletonLoader /> :

          isExpanded ? (
            <div className="mb-4 bg-white border border-gray-200 rounded-md rounded-tr-none rounded-tl-none overflow-x-auto w-full">
              {filteredTestCases.length > 0 ? (
                <table className="section-based-test-case-table table-auto w-full min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {showSelectionCheckboxes && !activeTestCaseId && (
                        <th className="w-12 px-4 py-3 column-header">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            ref={(input) => {
                              if (input)
                                input.indeterminate = someSelected && !allSelected;
                            }}
                            onChange={() => onSelectAll(section.section_id)}
                            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                        </th>
                      )}
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider column-header">
                        ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider column-header whitespace-nowrap">
                        Title
                      </th>
                      {!activeTestCaseId && (
                        <>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider column-header">
                            Priority
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider column-header">
                            Labels
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide column-header">
                            References
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider column-header">
                            Automation
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider column-header">

                          </th>
                        </>
                      )}
                      {activeTestCaseId && (
                        <th className="px-4 py-3 w-8 column-header" />
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredTestCases.map((testCase) => {
                      const isSelected = selectedTestCases.includes(
                        testCase.test_case_id,
                      );
                      const isActive = activeTestCaseId === testCase.test_case_id;

                      return (
                        <tr
                          onMouseEnter={() => {
                            setHoveredTestCaseId(testCase.test_case_id)
                          }}
                          onMouseLeave={() => {
                            setHoveredTestCaseId(null)

                          }}
                          key={testCase.test_case_id}
                          style={{height:"54px"}}
                          className={`transition-colors test-case-row-slide-in group ${isActive ? "bg-indigo-50" : "hover:bg-gray-50"}`}
                        >
                          {showSelectionCheckboxes && !activeTestCaseId && (
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() =>
                                  onSelectTestCase(testCase.test_case_id)
                                }
                                onClick={(e) => e.stopPropagation()}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                              />
                            </td>
                          )}
                          <td className="px-4 py-3 text-sm font-medium td-default underline">
                            <button
                              onClick={() =>
                                navigate(
                                  `/test-cases/view/${testCase.test_case_id}?project_id=${projectId}&test_case_id=${testCase.test_case_id}`,
                                )
                              }
                              className="font-medium text-left td-default underline"
                            >
                              {testCase.test_case_id?.substring(0, 8) || "N/A"}
                            </button>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <button
                          style={{
                            width: "200px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}
                              onClick={() =>
                                navigate(
                                  `/test-cases/view/${testCase.test_case_id}?project_id=${projectId}&test_case_id=${testCase.test_case_id}`,
                                )
                              }
                              className="font-medium text-left td-default underline"
                            >
                              {testCase.title}
                            </button>
                          </td>

                          {/* Full columns — hidden in compact mode */}
                          {!activeTestCaseId && (
                            <>
                              <td className="px-4 py-3">
                                <span className="text-base font-medium capitalize text-gray-500">
                                  {testCase.priority}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex flex-wrap gap-1">
                                  {testCase.tags && testCase.tags.length > 0 ? (
                                    testCase.tags
                                      .slice(0, 2)
                                      .map((tag, idx) => (
                                        <LabelBadge key={idx} label={tag} />
                                      ))
                                  ) : (
                                    <span className="text-xs text-gray-400">-</span>
                                  )}
                                  {testCase.tags && testCase.tags.length > 2 && (
                                    <span className="text-xs text-gray-500">
                                      +{testCase.tags.length - 2}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                {testCase.references &&
                                  testCase.references.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {testCase.references
                                      .slice(0, 2)
                                      .map((ref, idx) => (
                                        <span
                                          key={idx}
                                          className="td-default underline"
                                        >
                                          {ref}
                                        </span>
                                      ))}
                                    {testCase.references.length > 2 && (
                                      <span className="td-default underline">
                                        +{testCase.references.length - 2}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="td-default">None</span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-sm font-medium capitalize text-gray-500">
                                  {testCase.automation_status}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end space-x-1 gap-1">
                                  {testCase.test_case_id === hoveredTestCaseId && <>
                                    {canEdit && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onEditTestCase(testCase);
                                        }}
                                        className="p-1.5 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                                        title="Edit"
                                      >
                                        <Edit2 className="w-4 h-4" />
                                      </button>
                                    )}
                                    {canDelete && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onDeleteTestCase(testCase);
                                        }}
                                        className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                        title="Delete"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (onOpenHistory)
                                          onOpenHistory(testCase.test_case_id);
                                      }}
                                      className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                      title="View History"
                                    >
                                      <History className="w-4 h-4" />
                                    </button>
                                  </>}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onViewTestCase(testCase);
                                    }}
                                    className="p-0.5 text-gray-400 hover:text-indigo-500 rounded transition-colors"
                                    title="Open this test case"
                                  >
                                    <ChevronRight className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </>
                          )}

                          {/* Compact mode: chevron on every row */}
                          {activeTestCaseId && (
                            <td className="px-2 py-3 text-right w-8">
                              {isActive ? (
                                // Active row: left chevron → click closes the panel
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onViewTestCase({
                                      ...testCase,
                                      __closePanel: true,
                                    });
                                  }}
                                  className="p-0.5 text-indigo-500 hover:text-indigo-700 rounded transition-colors"
                                  title="Close panel"
                                >
                                  <ChevronLeft className="w-4 h-4" />
                                </button>
                              ) : (
                                // Inactive row: right chevron → click switches to this test case
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onViewTestCase(testCase);
                                  }}
                                  className="p-0.5 text-gray-400 hover:text-indigo-500 rounded transition-colors"
                                  title="Open this test case"
                                >
                                  <ChevronRight className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  {searchQuery
                    ? "No test cases match your search"
                    : "No test cases in this section"}
                </div>
              )}

              {/* Add Case and Add Subsection Buttons - Always visible when section is expanded */}
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center gap-2" style={{ justifyContent: "space-between" }}>
                {canAdd && (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddCase(section);
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-[#A17561FF] border border-indigo-300 rounded-md hover:bg-[#825E4EFF] transition-colors"
                      title="Add test case to this section"
                    >
                      <Plus className="w-4 h-4 text-white" />
                      <span>Add Case</span>
                    </button>
                    <div className="w-0.5 h-6 bg-[#C5C1BFFF]"></div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddSubsection(section);
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-[#A17561FF] border border-indigo-300 rounded-md hover:bg-[#825E4EFF] transition-colors"
                      title="Add subsection to this section"
                    >
                      <Plus className="w-4 h-4 text-white" />
                      <span>Add Subsection</span>
                    </button>
                  </div>
                )}
                <Pagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(totalTestCases / 25)}
                  onPageChange={(page) => setCurrentPage(page)}
                  totalItems={totalTestCases}
                  itemsPerPage={25}
                />
              </div>
            </div>
          ) : null
      }


      {/* Child Sections - Recursive rendering with increased indentation */}
      {isExpanded && hasChildren && (
        <div className="ml-4">
          {section.children.map((childSection) => {
            const childTestCases = testCasesData[childSection.section_id] || [];
            return (
              <HierarchicalSectionRow
                filters={filters}
                branchId={branchId}
                navigate={navigate}
                projectId={projectId}
                key={childSection.section_id}
                onOpenHistory={onOpenHistory}
                testCasesData={testCasesData}
                selectedSection={selectedSection}
                setSearchQuery={setSearchQuery}
                setShowInactive={setShowInactive}
                showInactive={showInactive}
                onOpenFilter={onOpenFilter}
                setViewMode={setViewMode}
                viewMode={viewMode}
                onOpenAI={onOpenAI}
                section={childSection}
                level={level + 1}
                testCases={childTestCases}
                searchQuery={searchQuery}
                selectedTestCases={selectedTestCases}
                expandedSections={expandedSections}
                onToggleSection={onToggleSection}
                onSelectTestCase={onSelectTestCase}
                onSelectAll={onSelectAll}
                onViewTestCase={onViewTestCase}
                onEditTestCase={onEditTestCase}
                onDeleteTestCase={onDeleteTestCase}
                onAssignTestCase={onAssignTestCase}
                onEditSection={onEditSection}
                onAddCase={onAddCase}
                onAddSubsection={onAddSubsection}
                onDeleteSection={onDeleteSection}
                canEdit={canEdit}
                canDelete={canDelete}
                canAssign={canAssign}
                canAdd={canAdd}
                selectedSectionId={selectedSectionId}
                activeTestCaseId={activeTestCaseId}
                showSelectionCheckboxes={showSelectionCheckboxes}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Main Section-Based Test Case View Component
 */
const SectionBasedTestCaseView = ({
  projectId,
  selectedSectionId,
  sections = [],
  filters = {},
  branchId = null,
  externalTestCases = null,
  scrollToSectionId = null,
  onScrollHandled = null,
  activeTestCaseId = null, // compact mode: only ID+Title shown
  onViewTestCase,
  onEditTestCase,
  onDeleteTestCase,
  onAssignTestCase,
  onEditSection,
  onAddCase,
  onAddSubsection,
  onDeleteSection,
  canEdit = true,
  canDelete = true,
  canAssign = true,
  canAdd = true,
  onOpenFilter = null,
  onOpenAI = null,
  onOpenHistory = null,
  exportSelectionMode = false,
  onSelectedTestCasesChange = null,
}) => {
  const [allTestCases, setAllTestCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTestCases, setSelectedTestCases] = useState([]);
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const navigate = useNavigate();
  useEffect(() => {
    if (onSelectedTestCasesChange) {
      onSelectedTestCasesChange(selectedTestCases);
    }
  }, [selectedTestCases, onSelectedTestCasesChange]);

  useEffect(() => {
    if (!exportSelectionMode && selectedTestCases.length > 0) {
      setSelectedTestCases([]);
    }
  }, [exportSelectionMode, selectedTestCases.length]);

  useEffect(() => {
    if (!scrollToSectionId) return;
    const el = document.querySelector(
      `[data-section-id="${scrollToSectionId}"]`,
    );
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (onScrollHandled) onScrollHandled();
  }, [scrollToSectionId, onScrollHandled]);

  // Default handler for assign if not provided
  const handleAssignTestCase = useCallback(
    (testCase) => {
      if (onAssignTestCase) {
        onAssignTestCase(testCase);
      } else {
        // Placeholder - can be replaced with actual assign modal/functionality
        logger.info(
          "[SectionBasedTestCaseView] Assign test case:",
          testCase.test_case_id,
        );
      }
    },
    [onAssignTestCase],
  );

  // Wrapper for delete handler that also updates local state
  const handleDeleteTestCase = useCallback(
    async (testCase) => {
      try {
        await onDeleteTestCase(testCase);
        // Immediately remove from local state
        setAllTestCases((prev) =>
          prev.filter((tc) => tc.test_case_id !== testCase.test_case_id),
        );
      } catch (error) {
        // Error handling is done in parent component
        throw error;
      }
    },
    [onDeleteTestCase],
  );

  // When externalTestCases provided by parent, use it directly — skip internal fetch
  useEffect(() => {
    if (externalTestCases !== null) {
      setAllTestCases(externalTestCases);
      setLoading(false);
    }
  }, [externalTestCases]);

  // Load test cases internally only when no externalTestCases provided
  useEffect(() => {
    if (externalTestCases !== null) return; // parent is managing data

    const fetchTestCases = async () => {
      if (!projectId || !selectedSectionId) {
        setAllTestCases([]);
        return;
      }

      setLoading(true);
      try {
        const response = await testcaseService.getTestCases(projectId, {
          section_id: selectedSectionId === "root" ? null : selectedSectionId,
          limit: 1000,
          page: 1,
          branch_id: branchId || filters.branch_id || undefined,
          is_active: showInactive ? undefined : true,
          ...filters,
        });

        let testCasesArray = [];
        if (
          response?.data?.test_cases &&
          Array.isArray(response.data.test_cases)
        ) {
          testCasesArray = response.data.test_cases;
        } else if (response?.data && Array.isArray(response.data)) {
          testCasesArray = response.data;
        } else if (Array.isArray(response)) {
          testCasesArray = response;
        }

        setAllTestCases(testCasesArray);
      } catch (error) {
        logger.error(
          "[SectionBasedTestCaseView] Error fetching test cases:",
          error,
        );
        setAllTestCases([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTestCases();
  }, [
    projectId,
    selectedSectionId,
    filters,
    showInactive,
    branchId,
    externalTestCases,
  ]);

  // Find section path helper function
  const findSectionPath = useCallback((sections, targetId, path = []) => {
    for (const section of sections) {
      if (section.section_id === targetId) {
        return [...path, section.section_id];
      }
      if (section.children && section.children.length > 0) {
        const found = findSectionPath(section.children, targetId, [
          ...path,
          section.section_id,
        ]);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Persist expanded sections to sessionStorage and auto-expand selected section
  useEffect(() => {
    const key = `qms_expanded_sections_${projectId}`;
    const saved = sessionStorage.getItem(key);
    let initialExpanded = new Set();

    if (saved) {
      try {
        initialExpanded = new Set(JSON.parse(saved));
      } catch (e) {
        logger.error(
          "[SectionBasedTestCaseView] Error loading expanded sections:",
          e,
        );
      }
    }

    // Always expand the selected section and its parents
    if (selectedSectionId && sections.length > 0) {
      const sectionPath = findSectionPath(sections, selectedSectionId);
      if (sectionPath) {
        sectionPath.forEach((sectionId) => initialExpanded.add(sectionId));
      }
    }

    setExpandedSections(initialExpanded);
  }, [projectId, selectedSectionId, sections, findSectionPath]);

  // Auto-expand selected section and its parents when section changes
  useEffect(() => {
    if (!selectedSectionId || sections.length === 0) return;

    const sectionPath = findSectionPath(sections, selectedSectionId);
    if (sectionPath) {
      setExpandedSections((prev) => {
        const next = new Set(prev);
        // Add all sections in the path (selected section and its parents)
        sectionPath.forEach((sectionId) => next.add(sectionId));
        return next;
      });
    }
  }, [selectedSectionId, sections, findSectionPath]);

  useEffect(() => {
    const key = `qms_expanded_sections_${projectId}`;
    sessionStorage.setItem(key, JSON.stringify([...expandedSections]));
  }, [expandedSections, projectId]);

  // Toggle section expand/collapse
  const toggleSection = useCallback((sectionId) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  // Handle test case selection
  const handleSelectTestCase = useCallback((testCaseId) => {
    setSelectedTestCases((prev) => {
      if (prev.includes(testCaseId)) {
        return prev.filter((id) => id !== testCaseId);
      } else {
        return [...prev, testCaseId];
      }
    });
  }, []);

  // Handle select all for a section
  const handleSelectAll = useCallback(
    (sectionId) => {
      if (!Array.isArray(allTestCases)) return;

      const sectionTestCases = allTestCases.filter(
        (tc) => tc.section_id === sectionId,
      );
      const sectionTestCaseIds = sectionTestCases.map((tc) => tc.test_case_id);

      const allSelected = sectionTestCaseIds.every((id) =>
        selectedTestCases.includes(id),
      );

      if (allSelected) {
        setSelectedTestCases((prev) =>
          prev.filter((id) => !sectionTestCaseIds.includes(id)),
        );
      } else {
        setSelectedTestCases((prev) => [
          ...new Set([...prev, ...sectionTestCaseIds]),
        ]);
      }
    },
    [allTestCases, selectedTestCases],
  );

  // Build test cases map by section with inactive filtering
  const testCasesBySection = useMemo(() => {
    const map = {};
    if (Array.isArray(allTestCases)) {
      allTestCases.forEach((tc) => {
        // Filter out inactive test cases if showInactive is false
        if (!showInactive && (tc.is_active === false || tc.is_active === 0)) {
          return;
        }
        if (!map[tc.section_id]) {
          map[tc.section_id] = [];
        }
        map[tc.section_id].push(tc);
      });
    }
    return map;
  }, [allTestCases, showInactive]);

  // Find the selected section in the tree
  const findSection = (sections, sectionId) => {
    for (const section of sections) {
      if (section.section_id === sectionId) return section;
      if (section.children) {
        const found = findSection(section.children, sectionId);
        if (found) return found;
      }
    }
    return null;
  };

  const selectedSection = selectedSectionId
    ? findSection(sections, selectedSectionId)
    : null;

  // Get all test cases for the selected section and its children
  const getAllSectionIds = (section) => {
    if (!section) return [];
    const ids = [section.section_id];
    if (section.children) {
      section.children.forEach((child) => {
        ids.push(...getAllSectionIds(child));
      });
    }
    return ids;
  };

  const relevantTestCases = useMemo(() => {
    if (!selectedSection || !Array.isArray(allTestCases)) return [];
    const sectionIds = getAllSectionIds(selectedSection);
    const filtered = allTestCases.filter((tc) => {
      // Filter by section
      if (!sectionIds.includes(tc.section_id)) return false;
      // Filter out inactive test cases if showInactive is false
      if (!showInactive && (tc.is_active === false || tc.is_active === 0))
        return false;
      return true;
    });
    return filtered;
  }, [selectedSection, allTestCases, showInactive]);

  if (!selectedSectionId || !selectedSection) {
    // When externalTestCases provided, build a virtual root "All Test Cases" node
    // using the real section tree as children, and render it as one HierarchicalSectionRow
    if (sections.length > 0) {
      // Build a map: section_id -> test cases from the flat list
      const tcBySection = {};
      // externalTestCases.forEach(tc => {
      //   if (!showInactive && (tc.is_active === false || tc.is_active === 0)) return;
      //   if (!tcBySection[tc.section_id]) tcBySection[tc.section_id] = [];
      //   tcBySection[tc.section_id].push(tc);
      // });

      // Virtual root node wrapping all top-level sections as children
      const virtualRoot = {
        section_id: "__root__",
        name: "All Test Cases",
        children: sections,
      };

      // Auto-expand root and ALL sections (including nested) so empty sections are visible
      const collectAllIds = (list) => {
        const ids = [];
        list.forEach((s) => {
          ids.push(s.section_id);
          if (s.children && s.children.length > 0)
            ids.push(...collectAllIds(s.children));
        });
        return ids;
      };
      const allIds = new Set(["__root__", ...collectAllIds(sections)]);
      const mergedExpanded = new Set([...expandedSections, ...allIds]);

      console.log({ virtualRoot })
      return (
        <div className="space-y-4 w-full min-w-0">
          <HierarchicalSectionRow
            key="__root__"
            navigate={navigate}
            projectId={projectId}
            onOpenHistory={onOpenHistory}
            section={virtualRoot}
            setSearchQuery={setSearchQuery}
            selectedSection={selectedSection}
            setShowInactive={setShowInactive}
            showInactive={showInactive}
            onOpenFilter={onOpenFilter}
            setViewMode={setViewMode}
            viewMode={viewMode}
            onOpenAI={onOpenAI}
            level={0}
            testCasesData={testCasesBySection}
            searchQuery={searchQuery}
            selectedTestCases={selectedTestCases}
            expandedSections={mergedExpanded}
            onToggleSection={toggleSection}
            onSelectTestCase={handleSelectTestCase}
            onSelectAll={handleSelectAll}
            onViewTestCase={onViewTestCase}
            onEditTestCase={onEditTestCase}
            onDeleteTestCase={handleDeleteTestCase}
            onAssignTestCase={handleAssignTestCase}
            onEditSection={onEditSection}
            onAddCase={onAddCase}
            onAddSubsection={onAddSubsection}
            onDeleteSection={onDeleteSection}
            canEdit={canEdit}
            canDelete={canDelete}
            canAssign={canAssign}
            canAdd={canAdd}
            selectedSectionId={selectedSectionId}
            activeTestCaseId={activeTestCaseId}
            showSelectionCheckboxes={exportSelectionMode}
          />
        </div>
      );
    }

    // No section selected and no external data — show placeholder
    return (
      <div className="flex items-center justify-center h-96 text-gray-500">
        <div className="text-center">
          <Folder className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <p className="text-lg font-medium">
            Select a section to view test cases
          </p>
          <p className="text-sm mt-2">
            Choose a section from the left sidebar to get started
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-96 w-full p-4">
        <TableSkeleton rows={6} columns={5} />
      </div>
    );
  }

  // Show card-based empty state when section is selected but has zero test cases
  if (relevantTestCases.length === 0) {
    return (
      <div
        className="flex-1 flex items-center justify-center"
        style={{ minHeight: "calc(100vh - 300px)" }}
      >
        <TestCasesEmptyState
          onCreateSection={null} // Not needed when section is already selected
          onCreateTestCase={() => onAddCase(selectedSection)}
          onGenerateAI={onOpenAI || null}
          canCreateSection={false}
          canCreateTestCase={canAdd}
          canGenerateAI={!!onOpenAI}
          showAddSection={false}
        />
      </div>
    );
  }

  const hasSelectedTestCases =
    exportSelectionMode && selectedTestCases.length > 0;

  return (
    <div className="space-y-4 w-full min-w-0">
      {/* Controls Bar - Conditional: Selection Mode or Normal Mode */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 pb-4 border-b border-gray-200 min-w-0">
        {hasSelectedTestCases ? (
          /* Selection Mode Toolbar */
          <>
            {/* Left: Selection Count */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm font-medium text-gray-700">
                {selectedTestCases.length} test case
                {selectedTestCases.length !== 1 ? "s" : ""} selected
              </span>
              <button
                onClick={() => setSelectedTestCases([])}
                className="text-sm text-indigo-600 hover:text-indigo-700 hover:underline"
              >
                Clear
              </button>
            </div>

            {/* Right: Selection Actions */}
            <div className="flex items-center gap-2 flex-wrap">
              {canAssign && (
                <button
                  onClick={() => {
                    const selectedTCs = allTestCases.filter((tc) =>
                      selectedTestCases.includes(tc.test_case_id),
                    );
                    if (selectedTCs.length > 0) {
                      // Call assign for first selected, or implement bulk assign
                      handleAssignTestCase(selectedTCs[0]);
                    }
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  title="Assign selected test cases"
                >
                  <UserPlus className="w-4 h-4" />
                  <span>Assign To</span>
                </button>
              )}
              {canEdit && (
                <button
                  onClick={() => {
                    const selectedTCs = allTestCases.filter((tc) =>
                      selectedTestCases.includes(tc.test_case_id),
                    );
                    if (selectedTCs.length > 0) {
                      onEditTestCase(selectedTCs[0]);
                    }
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  title="Edit selected test case"
                >
                  <Edit2 className="w-4 h-4" />
                  <span>Edit</span>
                </button>
              )}
              {canDelete && (
                <button
                  onClick={async () => {
                    const selectedTCs = allTestCases.filter((tc) =>
                      selectedTestCases.includes(tc.test_case_id),
                    );
                    if (
                      selectedTCs.length > 0 &&
                      window.confirm(
                        `Delete ${selectedTCs.length} test case(s)?`,
                      )
                    ) {
                      const deletedIds = new Set();
                      try {
                        // Delete all selected test cases
                        await Promise.all(
                          selectedTCs.map(async (tc) => {
                            try {
                              await handleDeleteTestCase(tc);
                              deletedIds.add(tc.test_case_id);
                            } catch (error) {
                              // Error handling is done in parent component
                            }
                          }),
                        );
                        // Remove from selected list
                        setSelectedTestCases([]);
                      } catch (error) {
                        // Error handling is done in parent component
                      }
                    }
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 transition-colors"
                  title="Delete selected test cases"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete</span>
                </button>
              )}
            </div>
          </>
        ) : (
          /* Normal Mode Toolbar */
          <>
            {/* Left: Search */}
            <div className="w-full sm:w-auto sm:min-w-0 sm:flex-shrink">
              <div className="flex flex-row items-center w-full sm:w-auto">
                <input
                  type="text"
                  placeholder="Search by ID or Title..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-9 w-full sm:min-w-[160px] sm:max-w-[220px] pl-3 pr-3 py-2 text-sm border border-gray-300 rounded-md rounded-r-none focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                />
                <Search className="w-9 h-9 flex-shrink-0 px-2.5 py-2 bg-black text-gray-400 rounded-md rounded-l-none" />
              </div>
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap overflow-x-auto sm:overflow-visible">
              {/* Test Cases Button */}
              <button
                onClick={() => onAddCase(selectedSection)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-full button-brown transition-colors whitespace-nowrap flex-shrink-0"
              >
                <Plus className="w-4 h-4 flex-shrink-0" />
                <span>Test Cases</span>
              </button>

              {/* Test Case AI Button - Only show if onOpenAI is provided (enabled in project settings) */}
              {onOpenAI && (
                <button
                  onClick={() => {
                    if (onOpenAI) {
                      onOpenAI();
                    } else {
                      console.warn("Test Case AI handler not provided");
                    }
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-full button-red transition-colors whitespace-nowrap flex-shrink-0"
                  title="Generate test cases using AI"
                >
                  <Sparkles className="w-4 h-4 flex-shrink-0" />
                  <span>Test Case AI</span>
                </button>
              )}

              {/* Display Inactive Toggle */}
              <button
                onClick={() => setShowInactive(!showInactive)}
                className="flex items-center gap-2 px-2.5 py-2 text-sm font-medium text-gray-700 transition-colors whitespace-nowrap flex-shrink-0"
                title="Display Inactive Test Cases"
              >
                <Switch
                  checked={showInactive}
                  onCheckedChange={() => setShowInactive(!showInactive)}
                />
                <span>Display Inactive</span>
              </button>

              {/* Filter Button */}
              <button
                onClick={() => {
                  if (onOpenFilter) {
                    onOpenFilter();
                  } else {
                    console.warn("Filter handler not provided");
                  }
                }}
                className="flex items-center gap-2 px-2.5 py-2 text-sm font-medium text-gray-700 transition-colors whitespace-nowrap flex-shrink-0"
                title="Filter test cases"
              >
                <ListFilterIcon className="w-4 h-4 flex-shrink-0" />
                <span>Filters</span>
              </button>

              {/* Export helper text */}
              {exportSelectionMode && (
                <div className="text-xs text-gray-600 px-2 py-1 bg-amber-50 border border-amber-100 rounded-md">
                  Select test cases to export. If none are selected, filters are
                  used. If no filters are applied, all test cases are exported.
                </div>
              )}

              {/* View Toggle */}
              <div className="flex rounded-md overflow-hidden flex-shrink-0">
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 ${viewMode === "list" ? "bg-transparent text-black" : "bg-white text-gray-700 hover:bg-gray-50"} transition-colors`}
                  title="List view"
                >
                  <LogsIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Hierarchical Section View */}
      <div className="space-y-2">
        <HierarchicalSectionRow
          navigate={navigate}
          projectId={projectId}
          onOpenHistory={onOpenHistory}
          section={selectedSection}
          setSearchQuery={setSearchQuery}
          selectedSection={selectedSection}
          setShowInactive={setShowInactive}
          showInactive={showInactive}
          onOpenFilter={onOpenFilter}
          setViewMode={setViewMode}
          viewMode={viewMode}
          onOpenAI={onOpenAI}
          level={0}
          testCasesData={testCasesBySection}
          testCases={testCasesBySection[selectedSection.section_id] || []}
          searchQuery={searchQuery}
          selectedTestCases={selectedTestCases}
          expandedSections={expandedSections}
          onToggleSection={toggleSection}
          onSelectTestCase={handleSelectTestCase}
          onSelectAll={handleSelectAll}
          onViewTestCase={onViewTestCase}
          onEditTestCase={onEditTestCase}
          onDeleteTestCase={handleDeleteTestCase}
          onAssignTestCase={handleAssignTestCase}
          onEditSection={onEditSection}
          onAddCase={onAddCase}
          onAddSubsection={onAddSubsection}
          onDeleteSection={onDeleteSection}
          canEdit={canEdit}
          canDelete={canDelete}
          canAssign={canAssign}
          canAdd={canAdd}
          selectedSectionId={selectedSectionId}
          activeTestCaseId={activeTestCaseId}
          showSelectionCheckboxes={exportSelectionMode}
        />
      </div>
    </div>
  );
};

export default SectionBasedTestCaseView;
